import {
	LineLoop,
	LineSegments,
	Mesh,
	Raycaster,
	Scene,
	Vector2,
	type Vector3
} from 'three';

import type {
	DebugManagerConfig,
	DebugSphereConfig,
	DebugLineConfig,
	DebugCircleConfig,
	DebugCylinderConfig,
	DebugDiscConfig,
	DebugPathConfig,
	DebugWireframeConfig
} from './types';
import {
	addSphereCfn,
	updateSphereCfn,
	removeSphereCfn,
	addCylinderCfn,
	addDiscCfn,
	addLineCfn,
	updateLineCfn,
	removeLineCfn,
	addPathCfn,
	addCircleCfn,
	updateCircleCfn,
	removeCircleCfn,
	addWireframeCfn,
	removeWireframeCfn,
	showTooltipCfn,
	hideTooltipCfn,
	hideAllTooltipsCfn,
	updateTooltipPositionsCfn,
	createTooltipCfn,
	removeTooltipCfn,
	enableHoverDetectionCfn,
	disableHoverDetectionCfn,
	setVisibleCfn,
	clearCfn,
	disposeCfn
} from './functions';

/**
 * DebugManager handles debug visualization:
 * - Debug spheres at positions
 * - Debug lines/vectors
 * - Debug circles
 * - Bounding box wireframes
 * - Tooltips on hover
 *
 * @example
 * ```typescript
 * const debugManager = new DebugManager({
 *   scene: scene,
 *   camera: camera,
 *   renderer: renderer,
 *   visible: true
 * });
 *
 * debugManager.addSphere({
 *   id: 'pivot',
 *   position: new Vector3(0, 0, 0),
 *   color: 0xff0000,
 *   label: 'Pivot Point'
 * });
 *
 * // Toggle visibility
 * debugManager.setVisible(false);
 *
 * // Cleanup
 * debugManager.dispose();
 * ```
 */
export class DebugManager {
	#config: DebugManagerConfig;
	#visible: boolean;

	// Debug objects
	#spheres = new Map<string, Mesh>();
	#lines = new Map<string, LineSegments>();
	#circles = new Map<string, LineLoop>();
	#wireframes = new Map<string, LineSegments>();
	#tooltips = new Map<string, HTMLDivElement>();

	// Sphere labels for tooltips
	#sphereLabels = new Map<string, string>();

	// Hover detection
	#raycaster = new Raycaster();
	#mousePosition = new Vector2();
	#hoveredSphereId: string | null = null;
	#hoverEnabled = false;
	#boundMouseMoveHandler: ((event: MouseEvent) => void) | null = null;

	constructor(config: DebugManagerConfig) {
		this.#config = config;
		this.#visible = config.visible ?? false;

		// Enable hover detection if starting visible
		if (this.#visible) {
			this.enableHoverDetection();
		}
	}

	// ============ Public API ============

	/**
	 * Get visibility state
	 */
	get visible(): boolean {
		return this.#visible;
	}

	/**
	 * Set visibility of all debug objects
	 */
	setVisible(visible: boolean): void {
		setVisibleCfn(this, { visible });
	}

	/**
	 * Set visibility of a specific debug object by ID.
	 * Works across all object types (spheres, lines, circles, wireframes).
	 */
	setObjectVisible(id: string, visible: boolean): void {
		const sphere = this.#spheres.get(id);
		if (sphere) { sphere.visible = visible; return; }

		const line = this.#lines.get(id);
		if (line) { line.visible = visible; return; }

		const circle = this.#circles.get(id);
		if (circle) { circle.visible = visible; return; }

		const wireframe = this.#wireframes.get(id);
		if (wireframe) { wireframe.visible = visible; }
	}

	/**
	 * Add a debug sphere
	 */
	addSphere(config: DebugSphereConfig): Mesh | null {
		return addSphereCfn(this, { config });
	}

	/**
	 * Add a debug cylinder (tracked via spheres map)
	 */
	addCylinder(config: DebugCylinderConfig): Mesh | null {
		return addCylinderCfn(this, { config });
	}

	/**
	 * Add a debug disc / circular plane (tracked via spheres map)
	 */
	addDisc(config: DebugDiscConfig): Mesh | null {
		return addDiscCfn(this, { config });
	}

	/**
	 * Update a debug sphere position
	 */
	updateSphere(id: string, position: Vector3): void {
		updateSphereCfn(this, { id, position });
	}

	/**
	 * Remove a debug sphere
	 */
	removeSphere(id: string): void {
		removeSphereCfn(this, { id });
	}

	/**
	 * Add a debug line
	 */
	addLine(config: DebugLineConfig): LineSegments | null {
		return addLineCfn(this, { config });
	}

	/**
	 * Add a debug path (multi-point polyline rendered as connected line segments)
	 */
	addPath(config: DebugPathConfig): LineSegments | null {
		return addPathCfn(this, { config });
	}

	/**
	 * Update a debug line
	 */
	updateLine(id: string, start: Vector3, end: Vector3): void {
		updateLineCfn(this, { id, start, end });
	}

	/**
	 * Remove a debug line
	 */
	removeLine(id: string): void {
		removeLineCfn(this, { id });
	}

	/**
	 * Add a debug circle
	 */
	addCircle(config: DebugCircleConfig): LineLoop | null {
		return addCircleCfn(this, { config });
	}

	/**
	 * Update a debug circle
	 */
	updateCircle(id: string, center: Vector3, radius: number): void {
		updateCircleCfn(this, { id, center, radius });
	}

	/**
	 * Remove a debug circle
	 */
	removeCircle(id: string): void {
		removeCircleCfn(this, { id });
	}

	/**
	 * Add a wireframe for an object's bounding box or from dimensions
	 */
	addWireframe(config: DebugWireframeConfig): LineSegments | null {
		return addWireframeCfn(this, { config });
	}

	/**
	 * Remove a wireframe
	 */
	removeWireframe(id: string): void {
		removeWireframeCfn(this, { id });
	}

	/**
	 * Show tooltip for a sphere at screen position
	 */
	showTooltip(id: string, screenX: number, screenY: number): void {
		showTooltipCfn(this, { id, screenX, screenY });
	}

	/**
	 * Hide tooltip
	 */
	hideTooltip(id: string): void {
		hideTooltipCfn(this, { id });
	}

	/**
	 * Hide all tooltips
	 */
	hideAllTooltips(): void {
		hideAllTooltipsCfn(this);
	}

	/**
	 * Update tooltip positions (call in render loop)
	 */
	updateTooltipPositions(): void {
		updateTooltipPositionsCfn(this);
	}

	/**
	 * Enable hover detection for debug spheres
	 */
	enableHoverDetection(): void {
		enableHoverDetectionCfn(this);
	}

	/**
	 * Disable hover detection
	 */
	disableHoverDetection(): void {
		disableHoverDetectionCfn(this);
	}

	/**
	 * Get all sphere IDs for hover detection
	 */
	getSphereIds(): string[] {
		return Array.from(this.#spheres.keys());
	}

	/**
	 * Get sphere mesh by ID
	 */
	getSphere(id: string): Mesh | undefined {
		return this.#spheres.get(id);
	}

	/**
	 * Register an external sphere for tooltip hover detection
	 * The sphere is NOT added to the scene - it's expected to already exist
	 * @param id Unique identifier for the sphere
	 * @param sphere The mesh to track for hover
	 * @param label Tooltip label to display on hover
	 */
	registerExternalSphere(id: string, sphere: Mesh, label: string): void {
		// Remove existing registration if present
		if (this.#spheres.has(id)) {
			this.unregisterExternalSphere(id);
		}

		// Register the sphere for hover detection (don't add to scene)
		this.#spheres.set(id, sphere);
		this.#sphereLabels.set(id, label);
		createTooltipCfn(this, { id });

		// Ensure hover detection is enabled if we're visible
		if (this.#visible && !this.#hoverEnabled) {
			this.enableHoverDetection();
		}
	}

	/**
	 * Unregister an external sphere from tooltip hover detection
	 * Does NOT remove the sphere from the scene
	 * @param id The sphere ID to unregister
	 */
	unregisterExternalSphere(id: string): void {
		// Remove from tracking maps but don't dispose the mesh (we don't own it)
		this.#spheres.delete(id);
		this.#sphereLabels.delete(id);
		removeTooltipCfn(this, { id });
	}

	/**
	 * Clear all debug objects
	 */
	clear(): void {
		clearCfn(this);
	}

	/**
	 * Dispose of all resources
	 */
	dispose(): void {
		disposeCfn(this);
	}

	// ============ Internal Accessors (for CFN files) ============

	/** Access scene */
	get scene(): Scene {
		return this.#config.scene;
	}

	/** Access config */
	get configAccess(): DebugManagerConfig {
		return this.#config;
	}

	/** Access hover state */
	get hoverEnabled(): boolean {
		return this.#hoverEnabled;
	}

	/** Access hovered sphere ID */
	get hoveredSphereId(): string | null {
		return this.#hoveredSphereId;
	}

	/** Access spheres map */
	get spheres(): Map<string, Mesh> {
		return this.#spheres;
	}

	/** Access lines map */
	get lines(): Map<string, LineSegments> {
		return this.#lines;
	}

	/** Access circles map */
	get circles(): Map<string, LineLoop> {
		return this.#circles;
	}

	/** Access wireframes map */
	get wireframes(): Map<string, LineSegments> {
		return this.#wireframes;
	}

	/** Access tooltips map */
	get tooltips(): Map<string, HTMLDivElement> {
		return this.#tooltips;
	}

	/** Access raycaster */
	get raycasterAccess(): Raycaster {
		return this.#raycaster;
	}

	/** Access mouse position */
	get mousePositionAccess(): Vector2 {
		return this.#mousePosition;
	}

	/** Access mouse move handler */
	get mouseMoveHandler(): ((event: MouseEvent) => void) | null {
		return this.#boundMouseMoveHandler;
	}

	/** Get all spheres as array */
	get allSpheres(): Mesh[] {
		return Array.from(this.#spheres.values());
	}

	/** Get sphere count */
	get sphereCount(): number {
		return this.#spheres.size;
	}

	// ============ Internal Mutators (for CFN files) ============

	setVisibilityState(visible: boolean): void {
		this.#visible = visible;
	}

	setHoverEnabled(enabled: boolean): void {
		this.#hoverEnabled = enabled;
	}

	setHoveredSphereId(id: string | null): void {
		this.#hoveredSphereId = id;
	}

	setMouseMoveHandler(handler: ((event: MouseEvent) => void) | null): void {
		this.#boundMouseMoveHandler = handler;
	}

	// Sphere registration
	hasSphere(id: string): boolean {
		return this.#spheres.has(id);
	}

	registerSphere(id: string, sphere: Mesh): void {
		this.#spheres.set(id, sphere);
	}

	unregisterSphere(id: string): void {
		this.#spheres.delete(id);
	}

	// Line registration
	hasLine(id: string): boolean {
		return this.#lines.has(id);
	}

	getLine(id: string): LineSegments | undefined {
		return this.#lines.get(id);
	}

	registerLine(id: string, line: LineSegments): void {
		this.#lines.set(id, line);
	}

	unregisterLine(id: string): void {
		this.#lines.delete(id);
	}

	// Circle registration
	hasCircle(id: string): boolean {
		return this.#circles.has(id);
	}

	getCircle(id: string): LineLoop | undefined {
		return this.#circles.get(id);
	}

	registerCircle(id: string, circle: LineLoop): void {
		this.#circles.set(id, circle);
	}

	unregisterCircle(id: string): void {
		this.#circles.delete(id);
	}

	// Wireframe registration
	hasWireframe(id: string): boolean {
		return this.#wireframes.has(id);
	}

	getWireframe(id: string): LineSegments | undefined {
		return this.#wireframes.get(id);
	}

	registerWireframe(id: string, wireframe: LineSegments): void {
		this.#wireframes.set(id, wireframe);
	}

	unregisterWireframe(id: string): void {
		this.#wireframes.delete(id);
	}

	// Tooltip registration
	hasTooltip(id: string): boolean {
		return this.#tooltips.has(id);
	}

	getTooltip(id: string): HTMLDivElement | undefined {
		return this.#tooltips.get(id);
	}

	registerTooltip(id: string, tooltip: HTMLDivElement): void {
		this.#tooltips.set(id, tooltip);
	}

	unregisterTooltip(id: string): void {
		this.#tooltips.delete(id);
	}

	clearTooltips(): void {
		this.#tooltips.clear();
	}

	// Sphere label registration
	getSphereLabel(id: string): string | undefined {
		return this.#sphereLabels.get(id);
	}

	registerSphereLabel(id: string, label: string): void {
		this.#sphereLabels.set(id, label);
	}

	unregisterSphereLabel(id: string): void {
		this.#sphereLabels.delete(id);
	}

	// Create tooltip (public for CFN access)
	createTooltip(id: string): void {
		createTooltipCfn(this, { id });
	}

	// Remove tooltip (public for CFN access)
	removeTooltip(id: string): void {
		removeTooltipCfn(this, { id });
	}
}
